import { useEffect, useRef, type ReactNode } from 'react';
import { LiveKitRoom } from '@livekit/components-react';
import { useLivekitTokenMutation } from '@/state/mutations/player/use-livekit-token';
import { resolveSharedLiveKitRoomPlan } from '@/helpers/preview/shared-livekit-room.helper';
import { SharedLiveKitRoomContextProvider, useHasSharedLiveKitRoom } from './shared-livekit-room.context';

export interface SharedLiveKitRoomProviderProps {
  /** `entityType:entityId:onAir`. Without one the subtree renders unconnected. */
  livekitRoomId?: string;
  onError?: (error: Error) => void;
  children: ReactNode;
}

/**
 * Owns one LiveKit connection for its whole subtree, for as long as it stays mounted.
 *
 * Previews come and go with the cue — layers empty out between takes, panels swap, pickers open and
 * close — so a connection owned by any single preview drops with it. Anchoring it above them keeps
 * the operator joined for the lifetime of the live context.
 *
 * The room element stays in the tree while the token is still loading (`connect` is simply false),
 * because mounting it only afterwards would remount every preview below and disconnect whatever
 * they had already joined.
 */
export function SharedLiveKitRoomProvider({ livekitRoomId, onError, children }: SharedLiveKitRoomProviderProps) {
  const hasAncestorRoom = useHasSharedLiveKitRoom();
  const identityRef = useRef('');
  const mutation = useLivekitTokenMutation();
  const mutateRef = useRef(mutation.mutate);

  useEffect(() => {
    mutateRef.current = mutation.mutate;
  }, [mutation.mutate]);

  useEffect(() => {
    if (!livekitRoomId || hasAncestorRoom) {
      return;
    }
    // Keep the identity across room changes so the server sees one operator, not one per reconnect.
    identityRef.current ||= `output-preview-${Date.now()}`;
    mutateRef.current({ livekitRoomId, participantIdentity: identityRef.current, mode: 'preview' });
  }, [livekitRoomId, hasAncestorRoom]);

  const plan = resolveSharedLiveKitRoomPlan({
    hasAncestorRoom,
    livekitRoomId,
    token: mutation.data?.token,
    serverUrl: mutation.data?.host,
  });

  if (plan.kind === 'passthrough') {
    return <>{children}</>;
  }

  return (
    <LiveKitRoom
      video={false}
      audio={false}
      connect={plan.connect}
      token={plan.token}
      serverUrl={plan.serverUrl}
      onError={onError}
      // The room element is a pure wrapper — it must not introduce a box into the host layout.
      style={{ display: 'contents' }}
    >
      <SharedLiveKitRoomContextProvider value={true}>{children}</SharedLiveKitRoomContextProvider>
    </LiveKitRoom>
  );
}
